import React, { useState, useContext, useEffect, useRef } from 'react';
import cx from 'classnames';
import { Redirect } from 'react-router';

import { useLoginState } from '@xingternal/hops';
import { LOGIN_STATE } from '@xingternal/xing-frame-provider';
import { XingFrameContext } from '@xingternal/xing-frame-provider';
import { GridContainer, Row, Column } from '@xingternal/xing-grid';
import Observer from '@xingternal/intersection-observer';

import {
  PageContext,
  EditContext,
  ModuleContextProvider,
  useEntityPageQuery,
} from '@xingternal/pages-common';
import * as Types from '@xingternal/pages-graphql-types';
import PageError, { ErrorBoundary } from '@xingternal/pages-page-error';
import {
  trackPageviewModules,
  trackModuleVisibleAction,
} from '@xingternal/pages-tracking';
import Header, { useCoverMedia, PreloadCover } from '@xingternal/pages-header';

import MainPage from './MainPage';
import MetaDataContainer, { MetaDataContainerProps } from './MetaDataContainer';
import { useModulesLoaded } from './helpers';
import Navigation from '../Navigation';
import EditBar from '../EditBar';
import CompanyBannersContainer from '../Banners/CompanyBannersContainer';
import CustomerFeedbackButton from '../CustomerFeedbackButton';
import {
  useNavigationActiveItem,
  useNavigationOffset,
  useUpdatePageContext,
  useUpdateEditContext,
  useAppcues,
} from '../../hooks';
import { getPathByFocusType } from '../../utils/getPathByFocusType';
import MODULES from '../../modulesMap';

import styles from './MainPage.css';

export type MainPageContainerProps = {
  match: {
    url: string;
  };
  pageSlug: string;
};

export type TrackedRef = {
  [key: string]: any;
  pageview?: boolean;
};

export type IntersectUseState = {
  [key: string]: boolean;
};

const isModuleActive = (moduleType: string, featureSwitches: string[]) => {
  // if the module is not tied to any feature switch return true
  if (!MODULES[moduleType].hasOwnProperty('featureSwitch')) {
    return true;
  }
  const featureSwitch = MODULES[moduleType].featureSwitch;
  return featureSwitch && featureSwitches.includes(featureSwitch);
};

const MainPageContainer = ({ match, pageSlug }: MainPageContainerProps) => {
  const { featureSwitches } = useContext(XingFrameContext);

  const loginState = useLoginState();

  const canEditMobile = featureSwitches?.includes('ep_mobileEdit');

  const isLoggedIn = loginState === LOGIN_STATE.loggedIn;
  const path = match.url.split('/')[1];
  const { data, loading } = useEntityPageQuery({
    variables: {
      id: pageSlug,
      socialProofClickReasonsKey:
        Types.ClickReasonsEnum.CrWebPublisherSocialProofHeader,
    },
    errorPolicy: 'all',
  });
  const coverMediaQueryResult = useCoverMedia(pageSlug);

  const tracked = useRef<TrackedRef>({});
  const [intersected, setIntersected] = useState<IntersectUseState>({});

  const { isEditing } = useContext(EditContext);
  const { setPageContext } = useContext(PageContext);

  let metaData: MetaDataContainerProps = {};
  let headerModule: Types.Maybe<Types.EntityPageHeaderModule> | undefined;
  let isCompanyDraft: boolean = false;
  let isEditor: boolean = false;

  const pageModules =
    data?.entityPageEX?.__typename === 'EntityPage' &&
    data.entityPageEX.modules!.collection.filter(
      (module) =>
        module &&
        module.type !== 'header' &&
        MODULES[module.type] &&
        isModuleActive(module.type, featureSwitches)
    );

  const pageModulesByType = pageModules
    ? (pageModules
        .map((pageModule) => pageModule && pageModule.type)
        .filter((type) => type) as string[])
    : null;

  const [canDisplayModule, setModuleLoaded] =
    useModulesLoaded(pageModulesByType);

  const pageContext = useUpdatePageContext(
    data?.entityPageEX as Types.EntityPageResult,
    data?.entityPageEX?.__typename === 'EntityPage'
      ? {
          hasNewsModule: Boolean(
            data?.entityPageEX.modules?.collection.filter(
              (page) => page?.type === 'news'
            ).length
          ),
          setModuleLoaded,
        }
      : {}
  );
  const isPageContextSet = !!pageContext && pageContext?.setModuleLoaded;

  const isEditContextSet = useUpdateEditContext(
    data?.entityPageEX as Types.EntityPageResult
  );

  if (data?.entityPageEX?.__typename === 'EntityPage') {
    isCompanyDraft =
      data.entityPageEX.publicationStatus === 'DRAFT' &&
      data.entityPageEX.focusType === Types.EntityPageFocusType.Company;
    isEditor = data.entityPageEX.userPageContext?.permissions.canEdit!;

    metaData = {
      title: data.entityPageEX.title,
      url: data.entityPageEX.links.self,
      image: data.entityPageEX.logoImage?.[0].url,
      focusType: data.entityPageEX.focusType,
    };

    headerModule = data.entityPageEX.modules?.collection.find(
      (pageModule) => pageModule?.__typename === 'EntityPageHeaderModule'
    ) as Types.EntityPageHeaderModule;
  }
  const [activeIndex, forceActiveIndex, setIntersectionRatio] =
    useNavigationActiveItem(pageModulesByType);
  const [
    mainPageRef,
    navigationRef,
    navigationOffset,
    frameSize,
    navigationBarHeight,
  ] = useNavigationOffset();

  useAppcues();
  useEffect(() => {
    if (data?.entityPageEX?.__typename === 'EntityPage') {
      if (data.entityPageEX.publicationStatus === 'CREATED') {
        window.location.assign(`/pages/new/company`);
      }
    }
  }, [data]);

  if (data?.entityPageEX?.__typename === 'EntityPageError') {
    return (
      <MainPage>
        <PageError type={data.entityPageEX.errorCode === 10404 ? 404 : 503} />
      </MainPage>
    );
  }

  if (data?.entityPageEX?.__typename === 'EntityPageMoved') {
    return <Redirect to={`/${path}/${data.entityPageEX.slug}`} />;
  }

  if (
    loading ||
    !isPageContextSet ||
    !isEditContextSet ||
    (data &&
      data?.entityPageEX?.__typename === 'EntityPage' &&
      data.entityPageEX.publicationStatus === 'CREATED')
  ) {
    return (
      <MainPage>
        <MetaDataContainer setDefault={true} />
        <Header pageSlug={pageSlug} isPageLoading={true} />
      </MainPage>
    );
  }

  if (data?.entityPageEX?.__typename !== 'EntityPage') {
    return (
      <MainPage>
        <PageError />
      </MainPage>
    );
  }

  if (getPathByFocusType() !== `/${path}`) {
    return (
      <MainPage>
        <PageError type={404} />
      </MainPage>
    );
  }

  if (!(pageModules && pageModules?.length > 0)) {
    return (
      <MainPage>
        <PageError />
      </MainPage>
    );
  }

  const handleModuleIntersect = (
    {
      isIntersecting,
      intersectionRatio,
    }: { isIntersecting: boolean; intersectionRatio: number },
    type: string,
    index: number
  ) => {
    setIntersectionRatio(type, intersectionRatio);

    if (!tracked.current[type] && isIntersecting) {
      tracked.current = { ...tracked.current, [type]: true };
      setIntersected({ ...intersected, [type]: true });
      trackModuleVisibleAction(type, index + 1);
    }
  };

  if (!tracked.current['pageview'] && isEditing !== null) {
    tracked.current = { ...tracked.current, pageview: true };

    const trackIsFollower: Boolean =
      data.entityPageEX.userPageContext?.userInteractions?.__typename ===
      'EntityPageUserInteractionFollow'
        ? Boolean(
            data.entityPageEX.userPageContext?.userInteractions.followState
              ?.isFollowing
          )
        : false;

    trackPageviewModules({
      isLoggedIn,
      isEditor,
      isEditing,
      modulesByType: pageModulesByType,
      id: data.entityPageEX.id.split('.')[0],
      focusType: metaData.focusType,
      contractType: data.entityPageEX.contract?.type,
      isFollowing: trackIsFollower,
    });
  }
  // PageContext.Provider is important here for SSR. It avoids to wait for the useEffect that executes on client side only
  return (
    <PageContext.Provider
      value={{ pageContext: pageContext, setPageContext: setPageContext }}
    >
      <MainPage mainPageRef={mainPageRef}>
        <MetaDataContainer {...metaData} />
        <PreloadCover queryResult={coverMediaQueryResult} />
        {isEditor &&
          data.entityPageEX.focusType === Types.EntityPageFocusType.Company && (
            <CompanyBannersContainer
              companyId={pageContext?.companyId}
              isDraft={isCompanyDraft}
            />
          )}

        {isEditor && <CustomerFeedbackButton module="main" />}

        {isEditor && (
          <EditBar
            pageSlug={pageSlug}
            displayNewsButtons={true}
            displayBackButton={false}
            contractType={data.entityPageEX.contract?.type}
            globalId={data.entityPageEX.globalId}
            logo={metaData.image}
            pageTitle={metaData.title}
            pageId={data.entityPageEX.id}
            displayManageUpdates={Boolean(
              pageModules.find((pageModule) => pageModule?.type === 'news')
            )}
            canEditOnMobile={canEditMobile}
          />
        )}

        <div
          className={cx(styles.headerWrapper, {
            [styles.isShowingCompanyBanner]:
              pageContext?.isShowingCompanyBanner,
          })}
        >
          <Header
            showKununu={Boolean(
              pageModules.find((pageModule) => pageModule?.type === 'kununu')
            )}
            entityPageData={data}
            pageSlug={pageSlug}
            isEditor={isEditor}
            moduleProperties={headerModule?.properties}
            isFreePage={data.entityPageEX.contract?.type === 'FREE'}
            coverMediaQueryResult={coverMediaQueryResult}
          />
        </div>
        <Navigation
          pageModulesByType={pageModulesByType!}
          navigationOffset={navigationOffset}
          frameSize={frameSize}
          activeIndex={activeIndex}
          forceActiveIndex={forceActiveIndex}
          navigationRef={navigationRef}
          navigationBarHeight={navigationBarHeight}
        />
        <GridContainer>
          <Row className={styles.row}>
            <Column size={12} className={styles.column}>
              {pageModules &&
                pageModules.map((pageModulesItem, i: number) => {
                  const { type, globalId, properties } =
                    pageModulesItem as Types.EntityPageGenericModule;
                  const Module = MODULES[type] ? MODULES[type].module : null;
                  const moduleContext = {
                    moduleProperties: properties,
                    intersected: type ? intersected[type] : null,
                    moduleType: type,
                    moduleGlobalId: globalId,
                  };

                  if (Module && type !== 'header') {
                    return (
                      <ModuleContextProvider value={moduleContext} key={i}>
                        <Observer
                          onChange={(event: any) =>
                            type && handleModuleIntersect(event, type, i)
                          }
                          rootMargin={`-${navigationOffset}px 0px 0px 0px`}
                          threshold={[0, 0.2, 0.4, 0.6, 0.7, 1]}
                        >
                          <div
                            id={`module-${type}`}
                            data-cy={`${type}Module`}
                            className={cx(styles.moduleWrapper, {
                              [styles.canDisplayModules]:
                                canDisplayModule(type),
                            })}
                          >
                            <ErrorBoundary
                              onError={() => setModuleLoaded(type)}
                            >
                              <Module
                                key={i}
                                isEditor={isEditor}
                                {...moduleContext}
                                {...pageContext}
                              />
                            </ErrorBoundary>
                          </div>
                        </Observer>
                      </ModuleContextProvider>
                    );
                  }
                  return null;
                })}
            </Column>
          </Row>
        </GridContainer>
      </MainPage>
    </PageContext.Provider>
  );
};

export default MainPageContainer;
